import logger from '../../common/logger';
import {
  AuthenticationCredential,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationCredential,
  TLoginMode,
} from './types';
import { convertCreationOptions, convertCredentialRequest } from './utils';

let abortController: AbortController | undefined;

export async function isBrowserSupported() {
  // Availability of `window.PublicKeyCredential` means WebAuthn is usable.
  // `isUserVerifyingPlatformAuthenticatorAvailable` means the feature detection is usable.
  if (window.PublicKeyCredential && PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
    // Check if user verifying platform authenticator is available.
    const authenticatorAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return authenticatorAvailable;
  }
  return false;
}

export async function isConditionalMediationAvailable() {
  if (window.PublicKeyCredential && PublicKeyCredential.isConditionalMediationAvailable) {
    const mediationAvailable = await PublicKeyCredential.isConditionalMediationAvailable();
    return mediationAvailable;
  }
  return false;
}

export async function createNewPasskey(publicKey: PublicKeyCredentialCreationOptionsJSON) {
  if (typeof abortController !== 'undefined') {
    // if necessary we can implement custom error as reason for aborting here for better error handling
    abortController.abort();
  }
  abortController = new AbortController();
  const options = convertCreationOptions(publicKey);
  options.signal = abortController.signal;
  logger(['Passkey: convertCreationOptions', options]);
  const credential = await navigator.credentials.create(options);
  abortController = undefined;
  return credential as RegistrationCredential | null;
}

export async function loginWithPasskey(publicKey: PublicKeyCredentialRequestOptionsJSON, mode?: TLoginMode) {
  if (typeof abortController !== 'undefined') {
    // if necessary we can implement custom error as reason for aborting here for better error handling
    abortController.abort();
  }
  abortController = new AbortController();
  const options = convertCredentialRequest(publicKey, mode);
  options.signal = abortController.signal;
  logger(['Passkey: credential request options:', options]);
  const credential = await navigator.credentials.get(options);
  abortController = undefined;
  return credential as AuthenticationCredential | null;
}

const AuthService = {
  isBrowserSupported,
  isConditionalMediationAvailable,
  createNewPasskey,
  loginWithPasskey,
};
export default AuthService;
